import { stopMessagePush } from '../gh_conversation/pushNotifications/messagePush.js';

/**
 * The mobile shell (gudhub-mobile/www/index.html) owns the session: it stores
 * the auth key in native storage and hands it to the web app in the URL
 * fragment on launch.
 *
 * That means the web app can't just clear localStorage when a session ends -
 * the native copy would survive and silently sign the user back in on the next
 * cold start. Both exits (an explicit log out, and a key the server has since
 * rejected) have to clear the native side and return control to the shell.
 *
 * Lives here rather than inside main.js's app.run() because two unrelated
 * places need it: the route guard in main.js and $scope.logout in
 * apps_manager/applications_list/applications_contrl.js. A function declared
 * inside app.run() is local to that closure - a second module calling it would
 * throw ReferenceError, which in the logout case swallows the whole promise
 * chain and leaves the user stuck on the page.
 *
 * Capacitor injects its bridge into allow-listed remote origins, so
 * window.Capacitor exists here even though this page is served from gudhub.com
 * rather than from the app bundle. On Android that takes gudhub-mobile's patch
 * of @capacitor/android (patches/), until Capacitor ships the fix itself.
 */

const NATIVE_AUTH_KEY = 'gh_auth_key';

/**
 * Clears the natively stored auth key and sends the WebView back to the login
 * shell.
 *
 * @returns {boolean} true if running inside the native app and the return was
 *   started - callers should stop their own navigation in that case. false on
 *   the plain web, where the caller should carry on as before.
 */
export function returnToNativeShell() {
    const Capacitor = window.Capacitor;

    if (!Capacitor || typeof Capacitor.isNativePlatform !== 'function' || !Capacitor.isNativePlatform()) {
        return false;
    }

    const Preferences = Capacitor.Plugins && Capacitor.Plugins.Preferences;

    // The native key, and this phone's direct-message notifications: a
    // signed-out phone must not go on showing the account's messages
    // (stopMessagePush never throws, and never waits long on the network).
    const cleared = Promise.allSettled([
        Preferences ? Preferences.remove({ key: NATIVE_AUTH_KEY }) : Promise.resolve(),
        stopMessagePush()
    ]);

    // .finally, not .then: a failure to clear must not strand the user on a
    // page they have just logged out of. Worst case the key survives and the
    // shell hands it over again, which the route guard will reject - a loop of
    // one, not a dead end.
    cleared.finally(() => {
        // The shell's own origin, as Capacitor's bridge reports it:
        // capacitor://localhost on iOS, https://localhost on Android (http only
        // before Capacitor 6 - and a shell opened on the wrong scheme gets no
        // bridge, so it could never sign the user in again).
        const local = window.WEBVIEW_SERVER_URL
            || (Capacitor.getPlatform() === 'ios' ? 'capacitor://localhost' : 'https://localhost');

        window.location.replace(local + '/index.html?signedout=1');
    });

    return true;
}

/**
 * Whether an updateToken() rejection should be treated as "this key is dead"
 * rather than "the network is down".
 *
 * Without this check a user in a lift loses their session: the request fails,
 * the guard reads it as a revoked key, and the native key is wiped along with
 * it - so they have to type their password again once signal returns, for a
 * key that was valid the whole time.
 *
 * @param {*} err  the rejection from authApi.updateToken
 */
export function isRejectedSession(err) {
    const status = err && (err.status !== undefined ? err.status : err.statusCode);

    // Angular's $http reports a transport failure (offline, DNS, CORS, abort)
    // as status <= 0. Anything else - including a missing status from a
    // non-$http rejection - is treated as a real rejection, keeping the
    // security-relevant case as the default.
    return status !== 0 && status !== -1;
}

/**
 * Pulls an auth key out of the launch URL and into localStorage.
 *
 * MUST run at module scope, before Angular bootstraps. The route guard in
 * app.run() checks localStorage.auth_key and redirects to /login when it is
 * empty - it runs before any $routeChangeStart listener, so a key parsed
 * inside routeChange() arrives too late to stop that redirect. Worse,
 * $location.path('/login') rewrites the URL through Angular's own encoder
 * before the listener ever sees it, so the fragment carrying the key can be
 * re-encoded or dropped on the way. Reading the raw URL first sidesteps both.
 *
 * Accepts the key from the fragment (#auth=, what the mobile shell sends -
 * fragments are never sent to the server, so the key stays out of access logs)
 * and from the query string (?auth=, kept for integrations that already use
 * it). Strips it from the address bar either way.
 */
export function captureHandoffAuthKey() {
    let url;

    try {
        url = new URL(window.location.href);
    } catch (e) {
        return false;
    }

    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));

    // URLSearchParams already percent-decodes, so the value is used as-is.
    // Decoding it a second time would corrupt any key containing a literal %.
    const authKey = url.searchParams.get('auth') || hashParams.get('auth');

    if (!authKey) {
        return false;
    }

    window.localStorage.setItem('auth_key', authKey);

    url.searchParams.delete('auth');
    hashParams.delete('auth');
    url.hash = hashParams.toString() ? '#' + hashParams.toString() : '';
    window.history.replaceState({}, document.title, url.toString());

    return true;
}
